import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { of, Observable, shareReplay, switchMap } from 'rxjs';

import { AuthenticationService } from './authentication.service';

import { environment } from '@environments/environment';
import { User } from '@core/models/user';

export interface ApiKey {
  id: number;
  name: string;
  expires?: Date;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private readonly endpoint = `${environment.apiUrl}/users`;
  readonly impersonatedUser$: Observable<User | undefined>;

  constructor(
    private authService: AuthenticationService,
    private http: HttpClient
  ) {
    this.impersonatedUser$ = this.authService.user.pipe(
      switchMap(token => {
        if (token?.impersonatorId == null || token.userId == null) {
          return of(undefined);
        }

        return this.getById(token.userId);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  createUser(newUser: { email: string, password: string, requirePasswordUpdate: boolean }) {
    return this.http.post(`${this.endpoint}`, newUser);
  }

  delete(id: number, body: { password?: string } = {}) {
    return this.http.delete(`${this.endpoint}/${id}`, { body });
  }

  update(id: number, body: {}) {
    return this.http.put(`${this.endpoint}/${id}`, body);
  }

  getAll(pageSize?: number, pageNumber?: number, role?: number, search?: string) {
    let params = new HttpParams();
    if (pageSize != undefined) params = params.set('pageSize', pageSize);
    if (pageNumber != undefined) params = params.set('pageNumber', pageNumber);
    if (role != undefined) params = params.set('role', role);
    if (search != undefined && search !== '') params = params.set('search', search);
    return this.http.get<User[]>(`${this.endpoint}`, { params, observe: 'response' } );
  }

  getUsersWithFields(fields?: string[]) {
    const fieldsParam = fields ? '?fields=' + fields.join(',') : '';
    return this.http.get<User[]>(`${this.endpoint}/all` + fieldsParam);
  }

  getNonAdminUsers() {
    let params = new HttpParams();
    params = params.set('role', 0);
    return this.http.get<User[]>(
      `${this.endpoint}`,
      {
        params,
        observe: 'response'
      }
    );
  }

  getById(id: number) {
    return this.http.get<User>(`${this.endpoint}/${id}`);
  }

  getByEmail(email: string) {
    return this.http.get<User>(`${this.endpoint}/username/${email}`);
  }

  getCurrentUser(): Observable<User | undefined> {
    const userId = this.authService.userValue?.userId;
    return userId == null ? of(undefined) : this.getById(userId);
  }

  getApiKeys() {
    return this.http.get<ApiKey[]>(`${this.endpoint}/api`);
  }

  getSerialKeys() {
    return this.http.get<any>(`${this.endpoint}/serial-keys`);
  }

  registerSerialKey(key: string) {
    return this.http.post<any>(`${this.endpoint}/register/${key}`, { });
  }

  deleteApiKey(id: number) {
    return this.http.delete(`${this.endpoint}/api/${id}`);
  }

  addApiKey(body: { name: string, expires?: Date }) {
    return this.http.post(`${this.endpoint}/api`, body);
  }
}
